/// <amd-module name="Wit/QueriesHub/Common/Components/ColumnOptionsPanelContainer" />
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ColumnOptionsPanel } from "Wit/Common/ColumnOptionsPanel/ColumnOptionsPanel";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IQueryService } from "Wit/Common/QueryService/IQueryService";
import { IQuerySortColumn } from "Wit/QueriesHub/Common/Interfaces";

export interface IColumnOptionsPanelContainerProps {
    onDismiss: () => void;
}

interface IColumnOptionTableItem {
    selectedField: ObservableValue<IFieldEntry | undefined>;
}

interface ISortColumnTableItem extends IColumnOptionTableItem {
    descending: ObservableValue<boolean>;
}

export class ColumnOptionsPanelContainer extends VssComponent<IColumnOptionsPanelContainerProps> {
    private queryService: IQueryService;
    private dropdownItems = new ObservableArray<IListBoxItem<IFieldEntry>>([]);
    private sortableDropdownItems = new ObservableArray<IListBoxItem<IFieldEntry>>([]);
    private columnTableItems = new ObservableArray<IColumnOptionTableItem>([]);
    private sortTableItems = new ObservableArray<ISortColumnTableItem>([]);
    private loading = new ObservableValue<boolean>(true);

    public constructor(props: IColumnOptionsPanelContainerProps, context: IVssComponentContext) {
        super(props, context);

        this.queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
    }

    public componentDidMount() {
        if (this.queryService.allFields.ready.value && this.queryService.queryResult.value) {
            this.onQueryUpdated();
        } else {
            if (!this.queryService.allFields.ready.value) {
                this.queryService.initializeFields();
            }

            if (!this.queryService.queryResult.value) {
                this.executeQuery();
            }
        }
    }

    private executeQuery = async () => {
        const projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";
        const wiql = await this.queryService.generateWiql(projectId);
        this.queryService.executeIfNotExecuting(projectId, wiql);
    };

    private getDropdownItems = (fields: IFieldEntry[]): IListBoxItem<IFieldEntry>[] => {
        return fields.map(field => ({
            id: field.id.toString(),
            text: field.name,
            data: field
        }));
    };

    private initializeColumnTableItems = (queryColumns: string[]) => {
        queryColumns.forEach(fieldReferenceName => {
            this.columnTableItems.push({
                selectedField: new ObservableValue<IFieldEntry | undefined>(
                    this.queryService.allFields.value.find(field => field.referenceName === fieldReferenceName)
                )
            });
        });
    };

    private initializeSortTableItems = (sortColumns: IQuerySortColumn[]) => {
        sortColumns.forEach(sortColumn => {
            const selectedField = this.queryService.sortableFields.value.find(field => field.referenceName === sortColumn.name);
            this.sortTableItems.push({
                selectedField: new ObservableValue<IFieldEntry | undefined>(selectedField),
                descending: new ObservableValue<boolean>(!!sortColumn.descending)
            });
        });
    };

    private onQueryUpdated = () => {
        if (!this.columnTableItems.length && this.queryService.queryResult.value && this.queryService.allFields.ready.value) {
            this.initializeColumnTableItems(this.queryService.queryColumns.value);
            this.initializeSortTableItems(this.queryService.queryResult.value.data!.sortColumns);
        }
        if (!this.dropdownItems.length && this.queryService.allFields.ready.value) {
            const items = this.queryService.isCrossProject ? this.queryService.editorFields.value : this.queryService.fieldProjectData.value.fields;
            this.dropdownItems.value = this.getDropdownItems(items);
            this.sortableDropdownItems.value = this.getDropdownItems(this.queryService.sortableFields.value);
        }
        if (this.queryService.queryResult.value && this.queryService.allFields.ready) {
            this.loading.value = false;
        }
        return true;
    };

    public render() {
        const { onDismiss } = this.props;
        return (
            <Observer
                queryResult={{ observableValue: this.queryService.queryResult, filter: this.onQueryUpdated }}
                queryColumns={{ observableValue: this.queryService.queryColumns, filter: this.onQueryUpdated }}
                fieldsReady={{ observableValue: this.queryService.allFields.ready, filter: this.onQueryUpdated }}
                dropdownItems={this.dropdownItems}
                sortableDropdownItems={this.sortableDropdownItems}
            >
                {({
                    dropdownItems,
                    sortableDropdownItems
                }: {
                    dropdownItems: IListBoxItem<IFieldEntry>[];
                    sortableDropdownItems: IListBoxItem<IFieldEntry>[];
                }) => (
                    <ColumnOptionsPanel
                        loading={this.loading}
                        onDismiss={onDismiss}
                        onSubmit={this.onSubmit}
                        columnTableItems={this.columnTableItems}
                        sortTableItems={this.sortTableItems}
                        dropdownItems={dropdownItems}
                        sortableDropdownItems={sortableDropdownItems}
                    />
                )}
            </Observer>
        );
    }

    private onSubmit = async () => {
        this.queryService.queryColumns.value = this.columnTableItems.value.map(tableItem => tableItem.selectedField.value!.referenceName);
        this.queryService.querySortColumns = this.sortTableItems.value.map(tableItem => ({
            name: tableItem.selectedField.value!.referenceName,
            descending: tableItem.descending.value
        }));
        this.queryService.isEditorDirty.value = true;
        const projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";
        const wiql = await this.queryService.generateWiql(projectId);
        this.queryService.executeQuery(projectId, wiql);
        this.props.onDismiss();
    };
}
